import {
  Box,
  IconButton,
  useTheme,
  Menu,
  MenuItem,
  useMediaQuery,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Slide,
} from "@mui/material";
import { useState, forwardRef } from "react";
import { tokens } from "../../theme";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import MenuOutlinedIcon from "@mui/icons-material/MenuOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useNavigate } from "react-router-dom";

// ✅ Animation de transition
const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// ✅ Composant popup de déconnexion
const ConfirmLogoutDialog = ({ open, onClose, onConfirm }) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      TransitionComponent={Transition}
      keepMounted
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "20px",
          backdropFilter: "blur(10px)",
          backgroundColor: "rgba(255, 255, 255, 0.9)",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.25)",
          p: 2,
        },
      }}
    >
      <DialogTitle sx={{ textAlign: "center", pb: 1 }}>
        <WarningAmberIcon color="warning" sx={{ fontSize: 48 }} />
      </DialogTitle>

      <DialogContent sx={{ textAlign: "center" }}>
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          Confirmer la déconnexion
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Souhaitez-vous vraiment vous déconnecter ? <br />
          Une nouvelle authentification sera nécessaire pour accéder à votre espace.
        </Typography>
      </DialogContent>

      <DialogActions sx={{ justifyContent: "center", mt: 1 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          color="primary"
          sx={{
            borderRadius: "10px",
            textTransform: "none",
            px: 3,
          }}
        >
          Annuler
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          color="error"
          sx={{
            borderRadius: "10px",
            textTransform: "none",
            px: 3,
          }}
        >
          Me déconnecter
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const Topbar = ({ setIsSidebar, onLogout, onMobileMenuToggle }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // ✅ États
  const [anchorEl, setAnchorEl] = useState(null);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  // ✅ Menu
  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  // ✅ Navigation profil
  const handleProfileClick = () => {
    handleMenuClose();
    navigate("/EditProfil");
  };

  // ✅ Ouvre la popup
  const handleLogoutClick = () => {
    handleMenuClose();
    setLogoutDialogOpen(true);
  };

  // ✅ Déconnexion confirmée
  const handleConfirmLogout = () => {
    setLogoutDialogOpen(false);
    localStorage.removeItem("token");
    localStorage.removeItem("user_role");
    localStorage.removeItem("user_data");
    setShowSuccess(true);
    setTimeout(() => {
      onLogout();
    }, 1500);
  };

  //  Fermer Snackbar
  const handleCloseSnackbar = () => setShowSuccess(false);

  return (
    <Box display="flex" justifyContent="space-between" alignItems="center" p={{ xs: 1, sm: 2 }}>

      {/* ICON PROFIL */}
      <Box display="flex" sx={{ ml: "auto", pr: { xs: 1, sm: 2 } }}>
        <IconButton
          onClick={handleMenuOpen}
          sx={{
            background:
              "linear-gradient(90deg, #2a5fa9 0%, #3a7fb5 20%, #4a9fc0 40%, #5ab8a0 60%, #70bd8d 80%, #8fc68b 100%)",
            color: "#fff",
            borderRadius: "50%",
            width: { xs: 36, sm: 40 },
            height: { xs: 36, sm: 40 },
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            "&:hover": {
              transform: "scale(1.1)",
              background:
                "linear-gradient(90deg, #1a4f99 0%, #2a6fa5 20%, #3a8fb0 40%, #4aa890 60%, #60ad7d 80%, #7fb67b 100%)",
              transition: "all 0.2s ease",
            },
          }}
        >
          <PersonOutlinedIcon />
        </IconButton>

       
        {/* MENU DÉROULANT */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          PaperProps={{
            sx: {
              borderRadius: "12px",
              backgroundColor: colors.primary[400],
              boxShadow: "0 6px 18px rgba(0, 0, 0, 0.2)",
              mt: 1,
              minWidth: isMobile ? "140px" : "180px",
              overflow: "hidden",
              transition: "all 0.3s ease-in-out",
            },
          }}
        >
          {/* === GESTION PROFIL === */}
          <MenuItem
            onClick={handleProfileClick}
            sx={{
              color: colors.grey[100],
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              py: isMobile ? 0.8 : 1,
              px: isMobile ? 1.5 : 2,
              fontSize: isMobile ? "0.85rem" : "0.95rem",
              transition: "all 0.25s ease",
              "&:hover": {
                background:
                  "linear-gradient(90deg, #1a4f99 0%, #2a6fa5 20%, #3a8fb0 40%, #4aa890 60%, #60ad7d 80%, #7fb67b 100%)",
                color: "#fff",
                transform: "scale(1.02)", 
              },
              "& svg": {
                fontSize: isMobile ? 18 : 20,
                transition: "transform 0.25s ease, color 0.25s ease",
              },
              "&:hover svg": {
                transform: "scale(1.2)",
                color: "#fff",
              },
            }}
          >
            <PersonOutlinedIcon fontSize="small" />
            <span>Gestion profil</span>
          </MenuItem>

          {/* === DÉCONNEXION === */}
          <MenuItem
            onClick={handleLogoutClick}
            sx={{
              color: colors.grey[100],
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              py: isMobile ? 0.8 : 1,
              px: isMobile ? 1.5 : 2,
              fontSize: isMobile ? "0.85rem" : "0.95rem",
              transition: "all 0.25s ease",
              "&:hover": {
                background:
                  "linear-gradient(90deg, #1a4f99 0%, #2a6fa5 20%, #3a8fb0 40%, #4aa890 60%, #60ad7d 80%, #7fb67b 100%)",
                color: "#fff",
                transform: "scale(1.02)",
              },
              "& svg": {
                fontSize: isMobile ? 18 : 20,
                transition: "transform 0.25s ease, color 0.25s ease",
              },
              "&:hover svg": {
                transform: "scale(1.2)",
                color: "#fff",
              },
            }}
          >
            <SettingsOutlinedIcon fontSize="small" />
            <span>Déconnexion</span>
          </MenuItem>
        </Menu>


      </Box>

      {/*  Utilisation du composant ConfirmLogoutDialog */}
      <ConfirmLogoutDialog
        open={logoutDialogOpen}
        onClose={() => setLogoutDialogOpen(false)}
        onConfirm={handleConfirmLogout}
      />

      {/*  Snackbar de succès */}
      <Snackbar
        open={showSuccess}
        autoHideDuration={2000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        TransitionComponent={Transition}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="success"
          sx={{ borderRadius: "10px", boxShadow: 3 }}
        >
          Déconnexion réussie
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Topbar;
